/**
 * The printing a card was produced in — "Normal", "Holofoil", "Reverse
 * Holofoil", "Normal - Japanese", "1st Edition".
 *
 * WHY THIS IS A MODULE AND NOT AN INLINE TERNARY. Six surfaces render a
 * listing's finish (the card-detail table and its mobile rows, the listing
 * page's spec list and buy bar, the cart lines, the seller's own listing
 * table) and they do NOT all render it the same way. The rule that decides
 * which is a judgement about the data, not about any one component, so it lives
 * here once rather than being re-derived six times and drifting.
 *
 * THE DATA. `CardListing.finish` is a `CharField(default="Normal")` mirroring
 * `catalog.CardPrice.finish`, which is the source `printing` verbatim. So the
 * value is free text, it is present on essentially every listing, and "Normal"
 * is overwhelmingly the commonest value. Measured on QA: 414,515 Normal price
 * rows, then Foil (221,213), 1st Edition (129,460), Unlimited (75,026),
 * Reverse Holofoil (56,299 across 13,773 cards).
 *
 * That distribution is the whole design. Printing the finish everywhere would
 * put a "Normal" pill on most rows in the app, which is noise that trains the
 * eye to skip the very field it is there to surface. Printing it nowhere is
 * what the app did, and it made a Reverse Holofoil listing indistinguishable
 * from a Normal one. So there are two answers, and the caller picks by whether
 * it has a labelled slot to fill:
 *
 *   finishLabel          a column, a spec row — something with a heading that
 *                        every row owes an answer to. A blank cell under
 *                        "Finish" reads as "unknown", which is a different
 *                        claim from "normal".
 *   distinguishingFinish a pill on a dense row, a meta line, a cart line —
 *                        somewhere the value competes for space with the
 *                        condition and the price. Show it only when it changes
 *                        what the buyer is looking at.
 */

/** The API's default, and the one value that distinguishes nothing on its own. */
export const NORMAL_FINISH = 'Normal';

/** The finish as printed text, or null when the API sent nothing usable. */
export function finishLabel(finish) {
  const value = String(finish ?? '').trim();
  return value || null;
}

/**
 * The finish, but only when it tells this listing apart from a default one.
 * Null for a bare "Normal" and for anything empty.
 *
 * EXACT MATCH, NEVER A PREFIX. "Normal - Japanese" begins with the default word
 * and is emphatically not the default — it is a different printing in a
 * different language, and it is the 7th commonest finish in QA (40,781 rows).
 * A `startsWith` here would silently hide every foreign-language normal
 * printing, which is the same bug this module exists to fix.
 */
export function distinguishingFinish(finish) {
  const value = finishLabel(finish);
  return value === NORMAL_FINISH ? null : value;
}
